"use client";

import { motion, useInView, useMotionValue, animate } from "framer-motion";
import React, { useEffect, useMemo, useRef, useState } from "react";

type CounterProps = {
  from?: number;
  to: number;
  duration?: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  className?: string;
};

const Counter: React.FC<CounterProps> = ({
  from = 0,
  to,
  duration = 1.6,
  decimals = 0,
  prefix = "",
  suffix = "",
  className = "",
}) => {
  const ref = useRef<HTMLSpanElement | null>(null);
  const isInView = useInView(ref, { once: true, margin: "-10% 0px" });

  const mv = useMotionValue(from);
  const [value, setValue] = useState<number>(from);

  useEffect(() => {
    const unsub = mv.on("change", (v) => setValue(v));
    return () => unsub();
  }, [mv]);

  useEffect(() => {
    if (!isInView) return;
    const controls = animate(mv, to, { duration, ease: "easeOut" });
    return () => controls.stop();
  }, [isInView, mv, to, duration]);

  const formatted = useMemo(() => {
    const n = decimals ? Number(value).toFixed(decimals) : Math.round(value);
    return decimals ? n : Number(n).toLocaleString();
  }, [value, decimals]);

  return (
    <motion.span ref={ref} className={className}>
      {prefix}
      {formatted}
      {suffix}
    </motion.span>
  );
};

export default Counter;
